import * as CSS from 'csstype';
import React, { forwardRef, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { ResponsiveValue, system } from 'styled-system';
import { JigsawComponent, solvSystem, sx } from '../../theme/system';
import { SolvTheme } from '../../theme/theme';
import { style } from '../../utils/css';

type GenericStackProps = PropsWithChildren<JigsawComponent> & {
  /** The amount of space (from the space scale) to put between each element */
  space?: ResponsiveValue<keyof typeof SolvTheme.space>;
};

const VStack = styled.div<GenericStackProps>(
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
  },
  system({
    space: {
      scale: 'space',
      property: '&& > *:not(:last-child)' as keyof CSS.Properties,
      transform: (value, scale) => ({
        marginBottom: scale?.[value],
      }),
    },
  }),
  solvSystem,
  sx,
);

const HStack = styled.div<GenericStackProps>(
  {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  (props) => style(props.sx ?? {}),
  system({
    space: {
      scale: 'space',
      property: '&& > *:not(:last-child)' as keyof CSS.Properties,
      transform: (value, scale) => ({
        marginRight: scale?.[value],
      }),
    },
  }),
  solvSystem,
  sx,
);

export type StackProps = GenericStackProps & {
  /** Whether to render a horizontal (row) stack or vertical */
  row?: boolean;
};

/**
 * The stack is a flex-column by default container that places space between each item within.
 */
const Stack = forwardRef(
  ({ row, className, children, ...props }: StackProps, ref) => {
    if (row)
      return (
        <HStack className={className} {...props} ref={ref}>
          {children}
        </HStack>
      );
    return (
      <VStack className={className} {...props} ref={ref}>
        {children}
      </VStack>
    );
  },
);

Stack.displayName = 'Stack';
export default Stack;
